import { Select, FilePicker } from './ui';
export type Question = {
  id: string;
  questionVersionId?: string;
  type: string;
  label: string;
  required: boolean;
  options?: { value: string; label: string }[];
  allowedMimeTypes?: string[];
  maxFiles?: number;
  maxFileSizeBytes?: number;
  minLength?: number;
  maxLength?: number;
  min?: string | null;
  max?: string | null;
};
export function ApplicationQuestion({
  question,
  value,
  onChange,
  onUpload,
  disabled,
  fileNames,
}: {
  question: Question;
  value: unknown;
  onChange: (value: unknown) => void;
  onUpload: (files: File[]) => void;
  disabled: boolean;
  fileNames?: string[];
}) {
  const common = { name: question.id, required: question.required, disabled };
  if (question.type === 'FILE')
    return (
      <FilePicker
        {...common}
        label={question.label}
        description={question.allowedMimeTypes?.join(', ') || 'Прикрепите документ'}
        required={question.required && !fileNames?.length}
        accept={question.allowedMimeTypes?.join(',')}
        multiple={(question.maxFiles || 1) > 1}
        onChange={(event) => onUpload(Array.from(event.target.files || []))}
      />
    );
  if (question.type === 'BOOLEAN')
    return (
      <Select
        {...common}
        value={value === undefined ? '' : String(value)}
        onChange={(event) => onChange(event.target.value === 'true')}
      >
        <option value="" disabled>
          Выберите ответ
        </option>
        <option value="true">Да</option>
        <option value="false">Нет</option>
      </Select>
    );
  if (question.type === 'MULTI_CHOICE')
    return (
      <div className="filter-options">
        {question.options?.map((option) => (
          <label key={option.value}>
            <input
              type="checkbox"
              disabled={disabled}
              name={`${question.id}-${option.value}`}
              checked={Array.isArray(value) && value.includes(option.value)}
              onChange={(event) =>
                onChange(
                  event.target.checked
                    ? [...(Array.isArray(value) ? value : []), option.value]
                    : (Array.isArray(value) ? value : []).filter((item) => item !== option.value),
                )
              }
            />
            {option.label}
          </label>
        ))}
      </div>
    );
  if (question.type === 'SINGLE_CHOICE')
    return (
      <Select
        {...common}
        value={String(value || '')}
        onChange={(event) => onChange(event.target.value)}
      >
        <option value="" disabled>
          Выберите ответ
        </option>
        {question.options?.map((option) => (
          <option value={option.value} key={option.value}>
            {option.label}
          </option>
        ))}
      </Select>
    );
  if (question.type === 'LONG_TEXT')
    return (
      <textarea
        {...common}
        minLength={question.minLength}
        maxLength={question.maxLength}
        value={String(value || '')}
        onChange={(event) => onChange(event.target.value)}
      />
    );
  return (
    <input
      {...common}
      type={question.type === 'DATE' ? 'date' : 'text'}
      min={question.min || undefined}
      max={question.max || undefined}
      minLength={question.minLength}
      maxLength={question.maxLength}
      value={String(value || '')}
      onChange={(event) => onChange(event.target.value)}
    />
  );
}

export function applicationAnswers(
  questions: Question[],
  values: Record<string, unknown>,
  files: Record<string, string[]>,
): Record<string, unknown>[] {
  return questions.flatMap<Record<string, unknown>>((question) => {
    const value = values[question.id];
    if (question.type === 'FILE') {
      if (!files[question.id]?.length) {
        if (question.required) throw new Error(`Добавьте файл: ${question.label}`);
        return [];
      }
      return [{ questionId: question.id, type: question.type, fileIds: files[question.id] }];
    }
    if (value === undefined || value === '' || (Array.isArray(value) && !value.length)) {
      if (question.required) throw new Error(`Заполните поле: ${question.label}`);
      return [];
    }
    return [
      {
        questionId: question.id,
        type: question.type,
        ...(['SINGLE_CHOICE', 'MULTI_CHOICE'].includes(question.type)
          ? { values: Array.isArray(value) ? value : [value] }
          : { value }),
      },
    ];
  });
}
